import { Segment } from '../../stores/SegmentStore';
import { InternetCategory, InternetSpeed, InternetSpeedTab, InternetTech, ProductCard } from './InternetCalculatorInterfaces';

export const getInternetSpeed = (internetCategory: ProductCard) => {
  if (!internetCategory?.speed && !internetCategory?.name?.match(/\d+/)) {
    return 0;
  }
  return internetCategory.speed ? Number(internetCategory.speed.match(/\d+/)[0]) : Number(internetCategory.name.match(/\d+/)[0]);
};

export const isEqualUploadAndDownloadSpeed = (internet: ProductCard): boolean => {
  const { download, upload } = getInternetUpDownSpeed(internet);
  return download && upload && download === upload;
};

export const getInternetUpDownSpeed = (internet: ProductCard): InternetSpeed => {
  const { speed, name, productOfferingCode } = internet;
  const telia1 = productOfferingCode.includes('TELIA1');
  const internetSpeedRegex = /(\d+)\/(\d+)/;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, download, upload] = speed ? speed.match(internetSpeedRegex) : name.match(internetSpeedRegex) || [null, null, null];

  return { download, upload, telia1 };
};

export const hasOptics = (internet: ProductCard) => {
  return !!getInternetSpeed(internet) && isEqualUploadAndDownloadSpeed(internet) && !internet.plannedNetwork;
};

export const addSpecialRequestCardWhenNecessary = (speeds: ProductCard[], formName: string, segment: Segment) => {
  if (!!formName && speeds.some((internet) => hasOptics(internet)) && segment === 'BUSINESS') {
    speeds.unshift({
      specialRequest: true,
      speed: Number.MAX_SAFE_INTEGER.toString(),
    } as ProductCard);
  }
};

const MAX_CARDS_IN_TAB = 4;

export const evenOutCategoriesWhenNeeded = (smallerSpeed: ProductCard[], largerSpeed: ProductCard[], speed: ProductCard) => {
  if (smallerSpeed.length < MAX_CARDS_IN_TAB) {
    smallerSpeed.push(speed);
  } else {
    largerSpeed.push(smallerSpeed[0]);
    smallerSpeed.shift();
    smallerSpeed.push(speed);
  }
};

const defaultInternetSpeedTabs: InternetSpeedTab[] = [
  { capacity: 'LARGE', speed: 100 },
  { capacity: 'SMALL', speed: 20 },
];
export const getInternetCategories = (internetSpeeds: ProductCard[] = [], internetSpeedTabs: InternetSpeedTab[] = defaultInternetSpeedTabs) => {
  const largerCategories: InternetCategory = {
    id: 'larger-capacities',
    labelKey: 'internetAndTvCalculator.largerCapacities',
    internets: [],
  };
  const middleCategories: InternetCategory = {
    id: 'middle-capacities',
    labelKey: 'internetAndTvCalculator.middleCapacities',
    internets: [],
  };
  const smallerCategories: InternetCategory = {
    id: 'smaller-capacities',
    labelKey: 'internetAndTvCalculator.smallerCapacities',
    internets: [],
  };
  const largeSpeedTab = internetSpeedTabs.find((st) => st.capacity === 'LARGE');
  const smallSpeedTab = internetSpeedTabs.find((st) => st.capacity === 'SMALL');
  const sortedSpeeds = internetSpeeds.sort((a, b) => (getInternetSpeed(a) > getInternetSpeed(b) ? -1 : 1));
  const thereIsThreeTabs = internetSpeeds.length > MAX_CARDS_IN_TAB * 2;
  const isSuitableForLarge = (speed: number) => speed >= largeSpeedTab?.speed && largerCategories.internets.length < MAX_CARDS_IN_TAB;

  if (sortedSpeeds.length <= MAX_CARDS_IN_TAB) {
    return [{ ...largerCategories, internets: sortedSpeeds }];
  }

  largerCategories.internets = sortedSpeeds.splice(0, sortedSpeeds.length % MAX_CARDS_IN_TAB);
  sortedSpeeds.forEach((speed) => {
    const internetSpeed = getInternetSpeed(speed);
    if (thereIsThreeTabs) {
      if (isSuitableForLarge(internetSpeed)) {
        largerCategories.internets.push(speed);
      } else if (internetSpeed > smallSpeedTab?.speed && middleCategories.internets.length < MAX_CARDS_IN_TAB) {
        middleCategories.internets.push(speed);
      } else {
        evenOutCategoriesWhenNeeded(smallerCategories.internets, middleCategories.internets, speed);
      }
    } else {
      if (internetSpeed >= largeSpeedTab?.speed && largerCategories.internets.length < MAX_CARDS_IN_TAB) {
        largerCategories.internets.push(speed);
      } else {
        evenOutCategoriesWhenNeeded(smallerCategories.internets, largerCategories.internets, speed);
      }
    }
  });

  return [largerCategories, middleCategories, smallerCategories].filter((category) => category.internets.length);
};

export const getInternetTech = (name: string, fix4g: boolean): InternetTech => {
  if (name.includes(' 5G')) {
    return '5G';
  }
  if (fix4g) {
    return '4G';
  }
  return 'CABLE';
};
